/**
 * Shared outdoor-interest taxonomy + adventure-style vibes.
 *
 * Used by Onboarding and Edit Profile so the two stay in sync. Subcategory
 * slugs intentionally reuse the Adventure `activity` slugs where they line up
 * (hiking, camping, kayaking, fishing, shooting, climbing, biking,
 * off-roading, overlanding) so the recommendation engine keeps matching.
 */
import {
    Footprints, Tent, Waves, Fish, Target, Mountain, Bike, Car,
    Snowflake, Camera, Baby, Wrench, Plus,
} from "lucide-react";

export const INTEREST_CATEGORIES = [
    {
        key: "hiking-trails", label: "Hiking & Trails", icon: Footprints,
        helper: "Walks, day hikes, backpacking and trail running.",
        subs: [
            { slug: "hiking", label: "Hiking" },
            { slug: "backpacking", label: "Backpacking" },
            { slug: "trail-running", label: "Trail running" },
            { slug: "walking", label: "Walking" },
            { slug: "nature-walks", label: "Nature walks" },
            { slug: "geocaching", label: "Geocaching" },
            { slug: "orienteering", label: "Orienteering" },
        ],
    },
    {
        key: "camping", label: "Camping", icon: Tent,
        helper: "Tent, car, RV, vanlife and dispersed camping.",
        subs: [
            { slug: "camping", label: "Tent camping" },
            { slug: "car-camping", label: "Car camping" },
            { slug: "rv-camping", label: "RV camping" },
            { slug: "vanlife", label: "Vanlife" },
            { slug: "dispersed-camping", label: "Dispersed camping" },
            { slug: "road-trips", label: "Road trips" },
        ],
    },
    {
        key: "water", label: "Water Adventures", icon: Waves,
        helper: "Paddling, boating and getting in the water.",
        subs: [
            { slug: "kayaking", label: "Kayaking" },
            { slug: "canoeing", label: "Canoeing" },
            { slug: "paddleboarding", label: "Paddleboarding" },
            { slug: "rafting", label: "Rafting" },
            { slug: "tubing", label: "Tubing" },
            { slug: "boating", label: "Boating" },
            { slug: "sailing", label: "Sailing" },
            { slug: "swimming", label: "Swimming" },
            { slug: "snorkeling", label: "Snorkeling" },
        ],
    },
    {
        key: "fishing", label: "Fishing", icon: Fish,
        helper: "From the bank, a kayak, the fly, or the ice.",
        subs: [
            { slug: "fishing", label: "Fishing" },
            { slug: "fly-fishing", label: "Fly fishing" },
            { slug: "kayak-fishing", label: "Kayak fishing" },
            { slug: "bank-fishing", label: "Bank fishing" },
            { slug: "ice-fishing", label: "Ice fishing" },
            { slug: "bowfishing", label: "Bowfishing" },
        ],
    },
    {
        key: "shooting", label: "Shooting Sports", icon: Target,
        helper: "Range days, target shooting, archery, clay sports, and firearm training.",
        subs: [
            { slug: "shooting", label: "Target shooting" },
            { slug: "pistol", label: "Pistol shooting" },
            { slug: "rifle", label: "Rifle shooting" },
            { slug: "long-range", label: "Long-range" },
            { slug: "shotgun", label: "Shotgun sports" },
            { slug: "trap-skeet", label: "Trap / skeet" },
            { slug: "sporting-clays", label: "Sporting clays" },
            { slug: "archery", label: "Archery" },
            { slug: "three-gun", label: "3-Gun / competition" },
            { slug: "muzzleloader", label: "Black powder / muzzleloader" },
            { slug: "airgun", label: "Airgun / pellet" },
            { slug: "range-day", label: "Range day" },
            { slug: "firearm-training", label: "Firearm training" },
        ],
    },
    {
        key: "climbing", label: "Climbing & Rappelling", icon: Mountain,
        helper: "Rock, boulders, ropes and vertical terrain.",
        subs: [
            { slug: "climbing", label: "Rock climbing" },
            { slug: "bouldering", label: "Bouldering" },
            { slug: "rappelling", label: "Rappelling" },
            { slug: "canyoneering", label: "Canyoneering" },
            { slug: "mountaineering", label: "Mountaineering" },
        ],
    },
    {
        key: "biking", label: "Biking", icon: Bike,
        helper: "Two wheels — pedal or motor.",
        subs: [
            { slug: "biking", label: "Mountain biking" },
            { slug: "gravel-biking", label: "Gravel biking" },
            { slug: "road-cycling", label: "Road cycling" },
            { slug: "dirt-biking", label: "Dirt biking" },
            { slug: "motorcycling", label: "Motorcycling" },
        ],
    },
    {
        key: "off-roading", label: "Off-Roading & Overlanding", icon: Car,
        helper: "Trails, rigs and remote travel.",
        subs: [
            { slug: "off-roading", label: "Off-roading" },
            { slug: "overlanding", label: "Overlanding" },
            { slug: "atv-utv", label: "ATV / UTV" },
            { slug: "four-wheeling", label: "Four-wheeling" },
        ],
    },
    {
        key: "winter", label: "Winter Activities", icon: Snowflake,
        helper: "Snow days, slopes and cold-weather fun.",
        subs: [
            { slug: "skiing", label: "Skiing" },
            { slug: "snowboarding", label: "Snowboarding" },
            { slug: "snowshoeing", label: "Snowshoeing" },
            { slug: "snowmobiling", label: "Snowmobiling" },
            { slug: "winter-hiking", label: "Winter hiking" },
        ],
    },
    {
        key: "wildlife", label: "Wildlife & Photography", icon: Camera,
        helper: "Slow it down — watch, shoot, forage and gaze.",
        subs: [
            { slug: "birdwatching", label: "Birdwatching" },
            { slug: "wildlife-viewing", label: "Wildlife viewing" },
            { slug: "photography", label: "Photography" },
            { slug: "stargazing", label: "Stargazing" },
            { slug: "foraging", label: "Foraging" },
            { slug: "mushroom-hunting", label: "Mushroom hunting" },
        ],
    },
    {
        key: "family", label: "Family-Friendly Outdoors", icon: Baby,
        helper: "Easy, casual outings for kids, groups and dogs.",
        subs: [
            { slug: "picnics", label: "Picnics" },
            { slug: "playground", label: "Playground adventures" },
            { slug: "scenic-drives", label: "Scenic drives" },
            { slug: "outdoor-festivals", label: "Outdoor festivals" },
            { slug: "dog-outings", label: "Dog-friendly outings" },
        ],
    },
    {
        key: "skills", label: "Outdoor Skills", icon: Wrench,
        helper: "Cook, navigate, build and survive.",
        subs: [
            { slug: "outdoor-cooking", label: "Outdoor cooking" },
            { slug: "navigation", label: "Navigation" },
            { slug: "survival-skills", label: "Survival skills" },
            { slug: "bushcraft", label: "Bushcraft" },
            { slug: "campfire-cooking", label: "Campfire cooking" },
        ],
    },
    {
        key: "other", label: "Other / Custom", icon: Plus,
        helper: "Magnet fishing, rockhounding, spelunking — type your own.",
        isCustom: true,
        subs: [],
    },
];

// slug → label lookup across every category.
export const SUB_LABEL = INTEREST_CATEGORIES.reduce((acc, cat) => {
    (cat.subs || []).forEach((s) => { acc[s.slug] = s.label; });
    return acc;
}, {});

export function interestLabel(slug) {
    return SUB_LABEL[slug] || (slug || "").replace(/-/g, " ");
}

// 3-state interest signal.
export const INTEREST_VIBES = [
    { id: "love", label: "Love it" },
    { id: "interested", label: "Interested" },
    { id: "curious", label: "Curious" },
];
export const DEFAULT_VIBE = "interested";

// Experience-based "how do you like the outdoors" vibes (multi-select).
export const ADVENTURE_STYLES = [
    { slug: "chill", label: "Chill / Relaxed", desc: "Low-effort, scenic, casual" },
    { slug: "family-friendly", label: "Family-Friendly", desc: "Good for kids or groups" },
    { slug: "dog-friendly", label: "Dog-Friendly", desc: "Bring the pup" },
    { slug: "beginner", label: "Beginner-Friendly", desc: "New to this" },
    { slug: "fitness", label: "Fitness / Challenge", desc: "Hard hikes, long rides, climbs" },
    { slug: "social", label: "Social / Group", desc: "Meetups and shared trips" },
    { slug: "solo", label: "Solo Friendly", desc: "Quiet, individual outings" },
    { slug: "photography", label: "Photography Focused", desc: "Scenic views, wildlife, golden hour" },
    { slug: "skill-building", label: "Skill Building", desc: "Navigation, survival, camping" },
    { slug: "accessible", label: "Accessible / Low Mobility", desc: "Paved, easy access, low grade" },
    { slug: "budget", label: "Budget-Friendly", desc: "Free or low-cost" },
    { slug: "adrenaline", label: "Adrenaline / Extreme", desc: "Climbing, rafting, off-road" },
];
export const STYLE_LABEL = ADVENTURE_STYLES.reduce((acc, s) => { acc[s.slug] = s.label; return acc; }, {});

// ---------------------------------------------------------------------------
// Shared activity surfaces — the SINGLE source of truth for every page that
// shows "activity" options (Explore filters, Create Adventure picker, Landing
// ticker). Keeps them in lock-step with the Profile / Onboarding taxonomy.
// ---------------------------------------------------------------------------

// All real activity categories (drops the free-text "Other / Custom" bucket).
export const ACTIVITY_CATEGORIES = INTEREST_CATEGORIES.filter((c) => !c.isCustom);

// Flat list of every selectable activity, tagged with its parent category.
export const ALL_ACTIVITIES = ACTIVITY_CATEGORIES.flatMap((c) =>
    (c.subs || []).map((s) => ({ ...s, category: c.label, categoryKey: c.key }))
);

// category key -> array of its sub-activity slugs (for "match any in category").
export const categorySlugs = (key) => {
    const cat = ACTIVITY_CATEGORIES.find((c) => c.key === key);
    return (cat?.subs || []).map((s) => s.slug);
};

// Readable, complete label set for the Landing marquee.
export const TICKER_ACTIVITIES = ACTIVITY_CATEGORIES.map((c) => c.label);

// Typeahead helper used by Explore + the Create picker.
export function searchActivities(query, limit = 24) {
    const s = (query || "").trim().toLowerCase();
    if (!s) return [];
    return ALL_ACTIVITIES.filter(
        (a) => a.label.toLowerCase().includes(s) || a.category.toLowerCase().includes(s)
    ).slice(0, limit);
}
